import { memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@nextui-org/react';
import type { NewRequest } from '../types';
import { formatDate } from '../utils/formatters';
import { StatusLabel } from './StatusLabel';

interface NewRequestRowProps {
  request: NewRequest;
  isLast?: boolean;
}

export const NewRequestRow = memo(function NewRequestRow({
  request,
  isLast = false,
}: NewRequestRowProps) {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  const productName = request.product_name || '—';

  const handleRowClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (target.closest('button, [role="menu"], [role="menuitem"]')) {
        return;
      }
      setIsNavigating(true);
      navigate(`/third-party-risk/intake-review/${request.externally_filled_intake_form_id}`);
    },
    [navigate, request.externally_filled_intake_form_id]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (target.closest('button, [role="menu"], [role="menuitem"]')) {
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsNavigating(true);
        navigate(`/third-party-risk/intake-review/${request.externally_filled_intake_form_id}`);
      }
    },
    [navigate, request.externally_filled_intake_form_id]
  );

  return (
    <div
      role="row"
      aria-level={2}
      tabIndex={isNavigating ? -1 : 0}
      onClick={isNavigating ? undefined : handleRowClick}
      onKeyDown={isNavigating ? undefined : handleKeyDown}
      aria-busy={isNavigating}
      className={`flex flex-row items-center px-3 pr-4 py-3 gap-3 h-11 bg-[#F8FAFC] transition-colors ${
        isLast ? '' : 'border-b border-[#E2E8F0]'
      } ${isNavigating ? 'opacity-70 cursor-wait' : 'cursor-pointer hover:bg-[#EFF6FF]'}`}
    >
      {/* Indent spacer */}
      <div className="w-5 h-5 flex-shrink-0" />

      {/* Columns container */}
      <div className="flex flex-row items-center justify-between flex-1 gap-4">
        {/* Product Name Column (240px) */}
        <div className="flex items-center gap-2 w-[240px]">
          <div className="w-5 h-5 flex-shrink-0" />
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="font-['Inter'] font-normal text-sm leading-5 text-[#314158] truncate">
              {productName}
            </span>
          </div>
        </div>

        {/* Risk Column (160px) */}
        <div className="flex items-center w-[160px]">
          <span className="font-['Inter'] font-normal text-sm leading-5 text-[#314158]">—</span>
        </div>

        {/* Business Criticality Column (180px) */}
        <div className="flex items-center w-[180px]">
          <span className="font-['Inter'] font-normal text-sm leading-5 text-[#314158]">—</span>
        </div>

        {/* Status Column (160px) */}
        <div className="flex items-center w-[160px]">
          <StatusLabel status="new_request" />
        </div>

        {/* Last Assessed Column (160px) */}
        <div className="w-[160px]">
          <span className="font-['Inter'] font-normal text-sm leading-5 text-[#314158]">
            {formatDate(request.submitted_at)}
          </span>
        </div>

        {/* Actions Column (20px) */}
        <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
          {isNavigating ? (
            <Spinner size="sm" color="primary" />
          ) : null}
        </div>
      </div>
    </div>
  );
});
